import React, { Fragment, PureComponent } from 'react'
import ReactSelect, { components } from 'react-select'
import { observer } from 'mobx-react'
import { isObservableArray } from 'mobx'
import * as R from 'ramda'

import HoverInfo from '../../UniformComponents/HoverInfo'
import Checkbox from './Checkbox'

import './styles/select.scss'

const CheckboxOption = (props) => (
    <components.Option {...props}>
        <Checkbox
            label={props.label}
            checked={props.isSelected}
            onBlur={() => null}
        />
    </components.Option>
)

const renderIcon = (option) => {
    if (!option || !option.src) return null
    return option?.isIcon
        ? <i className={`fa-lg fa-light ${option.src}`} alt="option-img" style={{ minWidth: '18px' }} />
        : <img src={option.src} alt="option-img" />
}

const Option = (props) => {
    const { options, label, value } = props
    const option = options.find((o) => o.value === value)
    let iconClass = R.pathOr(false, ['src'], option) ? 'has-icon' : ''
    iconClass += option?.iconLeft ? ' icon-left' : ' icon-right'

    return (
        <components.Option {...props}>
            <div className={`custom-img-option-container ${label ? '' : 'img-only'} ${iconClass}`}>
                {label ? (<p>{label}</p>) : null}
                {renderIcon(option)}
            </div>
        </components.Option>
    )
}

const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <div className="custom-dropdown-container">
            {
                props.selectProps.menuIsOpen
                    ? <i className="fa-regular fa-chevron-up" />
                    : <i className="fa-regular fa-chevron-down" />
            }
        </div>
    </components.DropdownIndicator>
)

const LockedDropdownIndicator = (props) => (

    <components.DropdownIndicator {...props}>
        <div className="custom-dropdown-container">
            <i className="fa-regular fa-lock" />
        </div>
    </components.DropdownIndicator>
)

const ClearIndicator = (props) => (
    <Fragment>
        {
            props.selectProps.isClearable ? (
                <div
                    className="custom-clear-icon"
                    ref={props.innerRef}
                    onClick={(e) => {
                        props.clearValue()
                    }}
                    {...props.innerProps}
                >
                    <i className="fa-regular fa-circle-xmark" />
                </div>
            ) : null
        }
        <hr className="custom-separator" />
    </Fragment>
)

const MultiValue = (props) => (
    <components.MultiValueContainer {...props}>
        <div className="custom-multi-value">
            <span className="multi-tag">{props.children}</span>
            <div
                className="remove-btn"
                onClick={(e) => {
                    props.removeProps.onClick()
                    e.stopPropagation()
                }}
            >
                <i className="fa-regular fa-circle-xmark" />
            </div>
        </div>
    </components.MultiValueContainer>
)

const MultiValueContainer = (props) => (
    <components.ValueContainer {...props}>
        <div className="custom-multi-value-container">
            {props.children}
        </div>
    </components.ValueContainer>
)

const CustomMultiNumSelectedPlaceholder = (props) => {
    const { getValue, children } = props
    const values = getValue()
    const count = values.length

    return (
        <components.Placeholder {...props}>
            {count > 0 ? `${count} selected` : children}
        </components.Placeholder>
    )
}

const SingleValueContainer = (props) => {
    const {
        options, getValue, label, children,
    } = props
    const selectedOption = getValue()[0]
    const option = selectedOption ? options.find((opt) => opt.value === selectedOption.value) : {}
    let iconClass = R.pathOr(false, ['src'], selectedOption) ? 'has-icon' : ''
    iconClass += option?.iconLeft ? ' icon-left' : ' icon-right'

    return (
        <components.ValueContainer {...props}>
            <div className={`custom-value-container ${label ? '' : 'img-only'} ${iconClass}`}>
                {children}
                {renderIcon(option)}
            </div>
        </components.ValueContainer>
    )
}
class Select extends PureComponent {
    value = undefined

    label = undefined

    constructor(props) {
        super(props)

        if (typeof props.register === 'function') {
            props.register(props.name, this.validate, props.path)
        }

        this.ref = React.createRef()

        this.state = { isInvalid: false }
    }

    componentWillUnmount() {
        const { unregister, name } = this.props

        if (typeof unregister === 'function') {
            unregister(name)
        }
    }

    validate = ({ value, name }, setState = false) => {
        const { isMandatory = false, validate } = this.props

        const hasValue = R.and(!R.isNil(value), !R.isEmpty(value))
        let isValid = !isMandatory || hasValue
        if (typeof validate === 'function') {
            isValid = isValid && validate({ value, name })
        }

        if (setState) {
            this.setState({
                isInvalid: !isValid,
            })
        }

        return isValid
    }

    onBlur = () => {
        const { name, onBlur } = this.props
        const { value, label, selectedOption } = this

        const hasChanged = !R.isNil(value) && !R.isNil(label)
        if (!hasChanged || !this.hasChanged) {
            return
        }

        this.setState({
            isInvalid: !this.validate({ value, name }),
        })

        this.hasChanged = false
        if (typeof onBlur === 'function') {
            onBlur({
                value, name, label, ...selectedOption,
            }, R.or(this.changeMeta, {}))
        }
    }

    onChange = (obj, { action, option }) => {
        const {
            name, onBlur, blurOnClear, isMulti,
        } = this.props
        this.value = isMulti ? obj : R.pathOr(null, ['value'])(obj)
        this.label = isMulti ? obj.map((o) => o.label) : R.pathOr(null, ['label'])(obj)
        this.selectedOption = obj
        this.hasChanged = true

        this.changeMeta = {
            action,
            option,
        }

        if (action === 'clear' || action === 'remove-value') {
            if (blurOnClear && typeof onBlur === 'function') {
                onBlur({
                    value: this.value,
                    name,
                    label: this.label,
                })
            } else {
                setTimeout(() => this.ref.current.select.blur(), 2)
            }
        }
        // this.elem.blur()
    }

    getDefaultValue = () => {
        const {
            defaultValue, options, indentation, isMulti,
        } = this.props
        let defaultOption = null

        const flatOptions = indentation
            ? options.map((o) => (o.options ? o.options : o)).flat(2)
            : options

        if (!R.isNil(defaultValue) && options.length > 0) {
            defaultOption = isMulti
                ? flatOptions.filter((opt) => (defaultValue.includes(opt.value)))
                : flatOptions.find(({ value }) => value === defaultValue)
            if (defaultOption) {
                this.value = defaultValue
            }
        }

        return defaultOption
    }

    getValue = () => {
        const { value, options, indentation } = this.props

        const flatOptions = indentation
            ? options.map((o) => (o.options ? o.options : o)).flat(2)
            : options

        if (isObservableArray(value)) {
            const arrayOptions = new Set(value.slice())
            return flatOptions.filter(({ value }) => arrayOptions.has(value))
        }
        if (typeof value === 'object') {
            return value
        }

        return flatOptions.find((option) => option.value === value)
    }

    getCustomComps = () => {
        const {
            isMulti, lockedDropdown, customComponents, showNumberSelected,
        } = this.props
        if (customComponents) return customComponents
        if (isMulti) {
            const custComponents = {
                Option: CheckboxOption,
                DropdownIndicator: lockedDropdown ? LockedDropdownIndicator : DropdownIndicator,
                ClearIndicator,
                MultiValue,
                ValueContainer: MultiValueContainer,
            }
            if (showNumberSelected) custComponents.Placeholder = CustomMultiNumSelectedPlaceholder
            return custComponents
        }
        return ({
            Option,
            DropdownIndicator: lockedDropdown ? LockedDropdownIndicator : DropdownIndicator,
            ClearIndicator,
            ValueContainer: SingleValueContainer,
        })
    }

    renderInput = () => {
        const {
            name, isDisabled, placeholder, emptyError, options,
            isClearable, value, cssClass, label, menuPlacement, style,
            showDisabled, isMulti, showSelected, elClass, elClassPrefix,
            tooltipTitle, tooltipText, tooltipLinkLabel, tooltipLink,
            openMenuOnClick, isOptionSelected, isLoading, showNumberSelected,
        } = this.props
        const { isInvalid } = this.state

        const customComps = this.getCustomComps()

        const selectClass = isInvalid
            ? 'empty-error'
            : ''

        let elem = ''

        if (!isDisabled) {
            elem = (
                <div className={`selector ${cssClass} ${showDisabled ? 'isDisabled' : ''}`} onFocus={this.onFocus} style={style}>
                    {label && (
                        <div className="label-container">
                            {/* tootip is handled by using the absolute position for parent
                                and relative positions for child components. */}
                            <label
                                htmlFor={`select-${name}`}
                                className={`select-label ${tooltipText ? 'extended' : ''}`}
                            >
                                {label}
                            </label>
                            {tooltipText && (
                                <HoverInfo
                                    cssClass="hover-info hover-icon"
                                    title={tooltipTitle}
                                    desc={tooltipText}
                                    linkText={tooltipLinkLabel}
                                    href={tooltipLink}
                                    dir="right"
                                >
                                    <i className="info-icon fa-solid fa-circle-question" />
                                </HoverInfo>
                            )}

                        </div>

                    )}
                    <ReactSelect
                        className={`form-select-container ${selectClass} ${elClass || ''}`}
                        classNamePrefix={`form-select${elClassPrefix || ''}`}
                        name={name}
                        onChange={isMulti
                            ? (v, e) => {
                                this.onChange(v, e)
                                this.onBlur()
                            }
                            : this.onChange}
                        isDisabled={showDisabled}
                        isClearable={isClearable}
                        isSearchable
                        placeholder={placeholder}
                        options={options}
                        blurInputOnSelect={!isMulti}
                        defaultValue={this.getDefaultValue()}
                        value={this.getValue()}
                        onBlur={this.onBlur}
                        components={customComps}
                        menuPlacement={menuPlacement}
                        isMulti={isMulti}
                        closeMenuOnSelect={!isMulti}
                        hideSelectedOptions={!isMulti && !showSelected}
                        openMenuOnClick={openMenuOnClick}
                        isOptionSelected={isOptionSelected}
                        isLoading={isLoading}
                        controlShouldRenderValue={!showNumberSelected}
                    />
                    {(emptyError && isInvalid) && (
                        <p className="error-msgs">{emptyError}</p>
                    )}
                </div>
            )
        } else {
            const defaultValue = this.getDefaultValue()
            const value = R.pathOr('', ['label'])(defaultValue)
            elem = (
                <div style={{ flex: 1, position: 'relative' }}>
                    {value}
                </div>
            )
        }

        return elem
    }

    render() {
        return this.renderInput()
    }
}

export default observer(Select)
